import { get, post, del } from './api_helper';
import * as Utils from '../Utils';
import { orderMatchesSkuQuery, collectOrderSkus } from './order_item_helper';

/**
 * Get all orders (Admin only)
 * @param {string} token - Admin authentication token
 * @param {Object} params - Query parameters for filtering and pagination
 * @returns {Promise} API response with orders list
 */
export const getAllOrders = (token, params = {}) => {
    console.log('getAllOrders - Params:', params);
    console.log('getAllOrders - Token:', token ? 'Present' : 'Missing');
    // Send params in body with token
    return post('order/list', { ...params, token });
};

/**
 * Update order status (Admin only)
 * @param {string} orderId - Order ID to update
 * @param {string} status - New status
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response
 */
export const updateOrderStatus = (orderId, status, token) => {
    console.log('updateOrderStatus - OrderID:', orderId, 'Status:', status);
    return post('order/status', { orderId, status, token });
};

/**
 * Delete order (Admin only)
 * @param {string} orderId - Order ID to delete
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response
 */
export const deleteOrder = (orderId, token) => {
    console.log('deleteOrder - OrderID:', orderId);
    return del(`order/delete/${orderId}`, { token });
};

/**
 * Update status for multiple orders
 * @param {Array<string>} orderIds - Order IDs
 * @param {string} status - New status
 * @param {string} token - Admin token
 * @returns {Promise<{successCount: number, failCount: number, total: number}>}
 */
export const updateBulkOrderStatus = async (orderIds, status, token) => {
    const results = await Promise.allSettled(
        orderIds.map(orderId => updateOrderStatus(orderId, status, token))
    );

    const successCount = results.filter(
        result => result.status === 'fulfilled' && result.value?.success
    ).length;

    return {
        successCount,
        failCount: orderIds.length - successCount,
        total: orderIds.length
    };
};

/**
 * Delete multiple orders
 * @param {Array<string>} orderIds - Order IDs
 * @param {string} token - Admin token
 * @returns {Promise<{successCount: number, failCount: number, total: number}>}
 */
export const deleteOrders = async (orderIds, token) => {
    const results = await Promise.allSettled(
        orderIds.map(orderId => deleteOrder(orderId, token))
    );

    const successCount = results.filter(
        result => result.status === 'fulfilled' && result.value?.success
    ).length;

    return {
        successCount,
        failCount: orderIds.length - successCount,
        total: orderIds.length
    };
};

/**
 * Get single order by ID (customer-scoped — not for admin panel)
 * @param {string} orderId - Order ID
 * @param {string} token - Authentication token
 * @returns {Promise} API response with order details
 */
export const getOrderById = (orderId, token) => {
    console.log('getOrderById - OrderID:', orderId);
    return get(`order/${orderId}`, { token });
};

/**
 * Get single order by ID (Admin only)
 * GET /api/order/admin/:orderId
 * @param {string} orderId - Order ID
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response with data.order
 */
export const getAdminOrderById = async (orderId, token) => {
    console.log('getAdminOrderById - OrderID:', orderId);
    const v1 = await get(`v1/order/admin/${orderId}`, { token });
    if (v1?.success !== false && (v1?.data?.order || v1?.order || v1?.data)) {
        return v1;
    }
    return get(`order/admin/${orderId}`, { token });
};

/**
 * POST /api/order/admin/:orderId/shipment
 * Body may include shipcluesOrderId and shipcluesShipmentId. At least one is required.
 * Not deployed on production yet: a 404 means the ids were not stored.
 */
export const recordAdminShipment = async (orderId, ids, token) => {
    const body = {};
    if (ids?.shipcluesOrderId) body.shipcluesOrderId = String(ids.shipcluesOrderId);
    if (ids?.shipcluesShipmentId) body.shipcluesShipmentId = String(ids.shipcluesShipmentId);

    const authToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('adminToken') : '');
    const response = await fetch(`${Utils.API_URL}order/admin/${orderId}/shipment`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify(body),
    });

    const raw = await response.text();
    let data = {};
    try {
        data = raw ? JSON.parse(raw) : {};
    } catch {
        data = { success: false, message: response.status === 404 ? 'Shipment record route is not deployed' : 'Could not record shipment' };
    }

    return { status: response.status, ...data };
};

/**
 * Get user orders
 * @param {string} userId - User ID
 * @param {string} token - User authentication token
 * @returns {Promise} API response with user orders
 */
export const getUserOrders = (userId, token) => {
    console.log('getUserOrders - UserID:', userId);
    return post('order/userorders', { userId, token });
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
    }).format(amount || 0);
};

/**
 * Derive order statistics from paginated rows when API summary is missing (legacy fallback).
 * Includes Cancelled/Refunded rows — do not use for GMV cards when summary is available.
 */
export const computeOrderStatistics = (orders = [], pagination = {}) => {
    const totalOrders = pagination?.totalOrders ?? orders.length;
    const totalRevenue = orders.reduce(
        (sum, order) => sum + Number(order?.total ?? order?.amount ?? 0),
        0
    );
    const averageOrderValue = orders.length ? totalRevenue / orders.length : 0;

    return {
        totalOrders,
        totalRevenue,
        averageOrderValue,
    };
};

/**
 * Client-side supplement when API `search` does not yet match line-item SKUs.
 * Keeps rows that match standard order fields OR any line-item SKU on the loaded page.
 */
export const filterOrdersForClientSearch = (orders = [], search = '') => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return orders;

    return orders.filter((order) => {
        const addr = order?.shippingAddress || order?.address || {};
        const blob = [
            order?.orderNumber,
            order?.invoiceNumber,
            addr.firstName,
            addr.lastName,
            addr.email,
            addr.phone,
            ...collectOrderSkus(order),
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
        return blob.includes(q) || orderMatchesSkuQuery(order, q);
    });
};

/**
 * Normalize GMV summary from POST /api/order/list (top-level or data.summary).
 */
export const normalizeOrderListSummary = (response) => {
    const raw = response?.summary || response?.data?.summary;
    if (!raw || typeof raw !== 'object') return null;

    const totalOrders = Number(raw.totalOrders) || 0;
    const totalRevenue = Number(raw.totalRevenue ?? raw.totalSales) || 0;
    const gmvOrders = Number(raw.gmvOrders) || 0;
    const averageOrderValue =
        raw.averageOrderValue != null
            ? Number(raw.averageOrderValue) || 0
            : gmvOrders > 0
              ? totalRevenue / gmvOrders
              : 0;

    return {
        totalOrders,
        totalRevenue,
        averageOrderValue,
        gmvOrders,
        collectedRevenue:
            raw.collectedRevenue != null ? Number(raw.collectedRevenue) || 0 : null,
        pendingCollection:
            raw.pendingCollection != null ? Number(raw.pendingCollection) || 0 : null,
        cancelledOrders: Number(raw.cancelledOrders) || 0,
        cancelledAmount: Number(raw.cancelledAmount) || 0,
    };
};

/**
 * Get status badge color
 * @param {string} status - Order status
 * @returns {string} Bootstrap badge color class
 */
export const getStatusBadgeColor = (status) => {
    const statusColors = {
        'Pending': 'warning',
        'Processing': 'info',
        'Shipped': 'primary',
        'Out for Delivery': 'info',
        'Delivered': 'success',
        'Cancelled': 'danger',
        'Refunded': 'secondary',
        'Failed': 'danger'
    };
    return statusColors[status] || 'secondary';
};

/**
 * Get payment status badge color
 * @param {string} status - Payment status
 * @returns {string} Bootstrap badge color class
 */
export const getPaymentStatusBadgeColor = (status) => {
    const statusColors = {
        'Pending': 'warning',
        'Completed': 'success',
        'Paid': 'success',
        'Failed': 'danger',
        'Cancelled': 'secondary',
        'Refunded': 'secondary',
        'Mismatch': 'danger'
    };
    return statusColors[status] || 'secondary';
};

const isPaymentConfirmed = (order) =>
    order?.payment === true ||
    ['Completed', 'Paid'].includes(order?.paymentStatus);

const isPaymentCancelled = (order) =>
    order?.paymentStatus === 'Cancelled' ||
    (order?.status === 'Cancelled' && isCashfreePayment(order));

const normalizePaymentMethod = (method) => String(method || '').trim().toLowerCase();

export const isCodPayment = (order) => normalizePaymentMethod(order?.paymentMethod) === 'cod';

export const isCashfreePayment = (order) => normalizePaymentMethod(order?.paymentMethod) === 'cashfree';

/**
 * Cashfree order with gateway session but payment not confirmed in DB.
 * Cancelled/abandoned checkouts are excluded — they show paymentStatus Cancelled.
 */
export const isCashfreeUnverifiedPayment = (order) => {
    if (!isCashfreePayment(order)) return false;
    if (isPaymentConfirmed(order)) return false;
    if (isPaymentCancelled(order)) return false;
    return Boolean(order?.cashfreeOrderId);
};

export const canVerifyCashfreePayment = (order) => {
    if (!isCashfreePayment(order)) return false;
    if (isPaymentConfirmed(order)) return false;
    if (isPaymentCancelled(order)) return false;
    if (order?.paymentStatus === 'Mismatch') return false;
    return Boolean(order?._id || order?.cashfreeOrderId);
};

export const hasFulfillmentPaymentMismatch = (order) => {
    const riskyStatuses = ['Shipped', 'Out for Delivery', 'Delivered'];
    return riskyStatuses.includes(order?.status) && isCashfreeUnverifiedPayment(order);
};

/**
 * Badge label + color for Payment column (COD Pending unchanged; Cashfree unverified is distinct).
 */
export const getPaymentDisplayForOrder = (order) => {
    const status = order?.paymentStatus || (order?.payment ? 'Completed' : 'Pending');

    if (order?.paymentStatus === 'Mismatch' || order?.paymentReviewRequired === true) {
        return {
            statusLabel: 'Payment review',
            statusBadge: 'danger',
            title: 'Payment amount mismatch detected — manual review required',
        };
    }

    if (order?.paymentStatus === 'Cancelled') {
        return {
            statusLabel: 'Cancelled',
            statusBadge: getPaymentStatusBadgeColor('Cancelled'),
            title: 'Cashfree checkout was cancelled or abandoned',
        };
    }

    if (isCashfreeUnverifiedPayment(order)) {
        const missingPaymentId = !order?.cashfreePaymentId;
        return {
            statusLabel: missingPaymentId ? 'Unverified payment' : 'Pending verification',
            statusBadge: 'danger',
            title:
                'Customer may have paid via Cashfree but the database still shows payment as unconfirmed. Use Verify Cashfree Payment.',
        };
    }

    if (isCodPayment(order) && status === 'Pending') {
        return {
            statusLabel: 'Pending',
            statusBadge: 'warning',
            title: 'COD — payment expected on delivery',
        };
    }

    return {
        statusLabel: status,
        statusBadge: getPaymentStatusBadgeColor(status),
        title: null,
    };
};

/**
 * Get available order statuses
 * @returns {Array} Array of order statuses
 */
export const getOrderStatuses = () => {
    return [
        'Pending',
        'Processing',
        'Shipped',
        'Out for Delivery',
        'Delivered',
        'Cancelled',
        'Refunded',
        'Failed'
    ];
};

/**
 * Get payment methods
 * @returns {Array} Array of payment methods
 */
export const getPaymentMethods = () => {
    return ['COD', 'Cashfree', 'Razorpay', 'Stripe'];
};

/**
 * Get payment statuses
 * @returns {Array} Array of payment statuses
 */
export const getPaymentStatuses = () => {
    return ['Pending', 'Completed', 'Paid', 'Failed', 'Cancelled', 'Refunded'];
};
